'use client';

/**
 * WorkspaceProvider — layout-level state that survives page navigation.
 *
 * Lifts chat, session, content tabs, file versions, and connection state
 * out of the workspace page component into a persistent context. This means
 * navigating to Val/Norma/Test Cases and back preserves artifact context at
 * the DB layer, but manual persona switches start a fresh side-panel chat.
 * Agent handoffs (route_handoff card) preserve the active conversation.
 */
import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, useState, useMemo, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useSession as useAuthSession } from 'next-auth/react';
import { useBackendConnection, useSession, useContentTabs, useChat, useConversationHistory, useFileVersions } from '@/hooks';
import { useSessionScopedUploads } from '@/hooks/use-session-scoped-uploads';
import { UploadedFile, FileVersion } from '@/types/chat';
import { apiClient } from '@/lib/api-client';
import { chatModeFromPathname, type ChatMode } from '@/lib/ai/prompts';
import { workspaceFromPathname } from '@/lib/workspaces/registry';
import { consumePreserveConversation } from '@/lib/persona-navigation';
import { rememberDeskSession, planDeskSwitch } from '@/lib/persona-desk-sessions';

// Pick the newest .xml entry from a versions list — used by the XML-restore
// fallback when a session blob loaded with no XML tab. Sort by dot-segmented
// version descending (e.g. "0.5" > "0.4").
function latestXmlVersion(versions: readonly FileVersion[]): FileVersion | undefined {
  const xmls = versions.filter(v => v.filename?.endsWith('.xml') || v.content_type === 'application/xml');
  if (!xmls.length) return undefined;
  return [...xmls].sort((a, b) => {
    const av = (a.version || '0').split('.').map(Number);
    const bv = (b.version || '0').split('.').map(Number);
    for (let i = 0; i < Math.max(av.length, bv.length); i++) {
      if ((bv[i] || 0) !== (av[i] || 0)) return (bv[i] || 0) - (av[i] || 0);
    }
    return 0;
  })[0];
}

// Re-export the shape for consumers
export interface WorkspaceState {
  // Mode
  chatMode: ChatMode;
  // Session
  sessionId: string;
  setSessionId: (id: string) => void;
  resetSession: () => string;
  switchSession: (id: string) => void;
  // Connection
  isConnected: boolean;
  isLoading: boolean;
  services: {
    ai: {
      isConnected: boolean;
    };
    data: {
      isConnected: boolean;
    };
  } | null;
  // Content tabs
  contentTabs: ReturnType<typeof useContentTabs>;
  // Chat
  chat: ReturnType<typeof useChat>;
  // Conversation history
  conversationHistory: ReturnType<typeof useConversationHistory>;
  // File versions
  fileVersions: ReturnType<typeof useFileVersions>['files'];
  allFileVersions: ReturnType<typeof useFileVersions>['allFiles'];
  isLoadingFiles: ReturnType<typeof useFileVersions>['isLoading'];
  fileVersionsLoadFailed: ReturnType<typeof useFileVersions>['loadFailed'];
  refreshFileVersions: ReturnType<typeof useFileVersions>['refresh'];
  // Uploaded files
  uploadedFiles: UploadedFile[];
  setUploadedFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  // Compli assessment context — latest assessment summary, injected into chat
  // memory so the AI can reference gaps/relevance in follow-up turns.
  assessmentContext?: string;
  setAssessmentContext: (ctx: string | undefined) => void;
  /** Session with an in-flight assistant stream (may differ from current). */
  streamingSessionId: string | null;
}
const WorkspaceContext = createContext<WorkspaceState | null>(null);
export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error("useWorkspace must be used within WorkspaceProvider");
  }
  return ctx;
}

/** Optional — returns null if not inside provider (safe for non-workspace pages) */
export function useWorkspaceOptional() {
  return useContext(WorkspaceContext);
}
export function WorkspaceProvider({
  children
}: {
  children: ReactNode;
}) {
  const {
    data: authSession
  } = useAuthSession();
  const userId = authSession?.user?.id ?? null;
  const pathname = usePathname();
  const router = useRouter();

  // ── Mode (derived from route) ──────────────────────────
  const chatMode = chatModeFromPathname(pathname);

  // ── Active page (for LLM context) ──────────────────────
  // Derived from the workspace registry (single source of truth). Fixes the
  // prior hand-rolled switch's drift bugs (AB#19236): it omitted
  // /tools/test-cases entirely and matched the legacy /study-design path
  // instead of /tools/study-design — so neither workspace ever injected its
  // <workspace_context> hint. The registry resolves all routes correctly.
  const activePage = workspaceFromPathname(pathname).id;

  // ── Session ─────────────────────────────────────────────
  const {
    sessionId,
    setSessionId,
    resetSession,
    switchSession
  } = useSession({
    userId
  });

  // Tracks which conversation owns a live stream — survives session switches
  // so the sidebar can show "Generating" on a background thread.
  const [streamingSessionId, setStreamingSessionId] = useState<string | null>(null);
  const reportStreamingSession = useCallback((sid: string, active: boolean) => {
    setStreamingSessionId(prev => {
      if (active) return sid;
      return prev === sid ? null : prev;
    });
  }, []);

  // ── Backend connection ──────────────────────────────────
  const {
    isConnected,
    isLoading,
    services
  } = useBackendConnection();
  const authReady = !!userId && isConnected;
  useEffect(() => {
    if (!streamingSessionId || !authReady) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch(`/api/chat-v3/${streamingSessionId}/active`);
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as {
          active?: boolean;
        };
        if (!data.active && !cancelled) {
          setStreamingSessionId(prev_0 => prev_0 === streamingSessionId ? null : prev_0);
        }
      } catch {
        // Non-fatal — indicator clears on next successful poll or remount.
      }
    };
    void poll();
    const id = window.setInterval(poll, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [streamingSessionId, authReady]);

  // ── Conversation history (cross-session: the list of all
  //    conversations must survive a switch) ────────────────
  const conversationHistory = useConversationHistory({
    userId,
    currentSessionId: sessionId,
    isConnected
  });

  // Manual nav-rail clicks restore each persona's desk session (or create one
  // on first visit). Contextual navigation sets preserveConversation instead.
  const prevWorkspaceRef = useRef(activePage);
  const prevSessionRef = useRef(sessionId);
  useEffect(() => {
    const prevWorkspace = prevWorkspaceRef.current;
    const prevSession = prevSessionRef.current;
    prevWorkspaceRef.current = activePage;
    prevSessionRef.current = sessionId;
    const preserveConversation = consumePreserveConversation();
    if (preserveConversation) {
      rememberDeskSession(userId, activePage, sessionId);
      return;
    }
    if (prevWorkspace === activePage) {
      if (prevSession !== sessionId) {
        rememberDeskSession(userId, activePage, sessionId);
      }
      return;
    }
    const plan = planDeskSwitch({
      userId,
      prevWorkspaceId: prevWorkspace,
      nextWorkspaceId: activePage,
      prevSessionId: prevSession,
      currentSessionId: sessionId,
      preserveConversation
    });
    if (plan.action === 'switch') {
      switchSession(plan.sessionId);
      return;
    }
    if (plan.action === 'create') {
      const newSessionId = resetSession();
      rememberDeskSession(userId, activePage, newSessionId);
      // Sidebar index entry is deferred until the first message — see
      // use-workspace-handlers conversation registration effect.
    }
  }, [activePage, sessionId, resetSession, switchSession, isConnected, userId, conversationHistory]);

  // ── navigateToCompli — lifted to WorkspaceProvider so router is in scope,
  //    then passed down as a prop so SessionScope can forward it to useChat
  //    without needing its own router reference.
  const navigateToCompli = useCallback(() => {
    router.push('/tools/compli');
  }, [router]);

  // key={sessionId} is the structural fix for the session-state bug
  // family: every conversation-scoped hook (chat refs, content tabs,
  // uploads, file versions) lives inside SessionScope, so switching
  // sessions REMOUNTS them — nothing survives a switch by construction,
  // instead of by per-handler scrubbing. Sub-route navigation within one
  // persona keeps the same sessionId. Manual cross-persona nav restores each
  // persona's desk session (see persona-desk-sessions); handoffs preserve the
  // active thread across personas.
  return <SessionScope key={sessionId} sessionId={sessionId} setSessionId={setSessionId} resetSession={resetSession} switchSession={switchSession} streamingSessionId={streamingSessionId} reportStreamingSession={reportStreamingSession} chatMode={chatMode} activePage={activePage} isConnected={isConnected} isLoading={isLoading} services={services} authReady={authReady} conversationHistory={conversationHistory} navigateToCompli={navigateToCompli}>
      {children}
    </SessionScope>;
}
interface SessionScopeProps {
  sessionId: string;
  setSessionId: (id: string) => void;
  resetSession: () => string;
  switchSession: (id: string) => void;
  streamingSessionId: string | null;
  reportStreamingSession: (sessionId: string, active: boolean) => void;
  chatMode: ChatMode;
  activePage: string;
  isConnected: boolean;
  isLoading: boolean;
  services: WorkspaceState['services'];
  authReady: boolean;
  conversationHistory: ReturnType<typeof useConversationHistory>;
  navigateToCompli: () => void;
  children: ReactNode;
}

/** Everything below is conversation-scoped — remounted per sessionId. */
function SessionScope(t0) {
  const $ = _c(56);
  const {
    sessionId,
    setSessionId,
    resetSession,
    switchSession,
    streamingSessionId,
    reportStreamingSession,
    chatMode,
    activePage,
    isConnected,
    isLoading,
    services,
    authReady,
    conversationHistory,
    navigateToCompli,
    children
  } = t0;
  const {
    uploadedFiles,
    setUploadedFiles,
    restoreFiles
  } = useSessionScopedUploads(sessionId);
  let t1;
  if ($[0] !== conversationHistory) {
    t1 = () => {
      conversationHistory.refresh();
    };
    $[0] = conversationHistory;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== authReady || $[3] !== restoreFiles || $[4] !== sessionId || $[5] !== t1 || $[6] !== uploadedFiles) {
    t2 = {
      sessionId,
      isConnected: authReady,
      uploadedFiles,
      onSessionSaved: t1,
      onFilesRestored: restoreFiles
    };
    $[2] = authReady;
    $[3] = restoreFiles;
    $[4] = sessionId;
    $[5] = t1;
    $[6] = uploadedFiles;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const contentTabs = useContentTabs(t2);
  const [assessmentContext, setAssessmentContext] = useState(undefined);
  const t3 = chatMode === "concierge" ? navigateToCompli : undefined;
  let t4;
  if ($[8] !== activePage || $[9] !== assessmentContext || $[10] !== authReady || $[11] !== chatMode || $[12] !== contentTabs.addComparatorTab || $[13] !== contentTabs.addOrUpdateContentTab || $[14] !== reportStreamingSession || $[15] !== sessionId || $[16] !== t3 || $[17] !== uploadedFiles) {
    t4 = {
      sessionId,
      isConnected: authReady,
      uploadedFiles,
      addOrUpdateContentTab: contentTabs.addOrUpdateContentTab,
      addComparatorTab: contentTabs.addComparatorTab,
      mode: chatMode,
      activePage,
      assessmentContext,
      onQualioTool: t3,
      reportStreamingSession
    };
    $[8] = activePage;
    $[9] = assessmentContext;
    $[10] = authReady;
    $[11] = chatMode;
    $[12] = contentTabs.addComparatorTab;
    $[13] = contentTabs.addOrUpdateContentTab;
    $[14] = reportStreamingSession;
    $[15] = sessionId;
    $[16] = t3;
    $[17] = uploadedFiles;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const chat = useChat(t4);
  let t5;
  if ($[19] !== authReady || $[20] !== sessionId) {
    t5 = {
      sessionId,
      isConnected: authReady
    };
    $[19] = authReady;
    $[20] = sessionId;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const {
    files: fileVersions,
    allFiles: allFileVersions,
    isLoading: isLoadingFiles,
    loadFailed: fileVersionsLoadFailed,
    refresh: refreshFileVersions
  } = useFileVersions(t5);
  let t6;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = new Set();
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const xmlRestoreAttemptedRef = useRef(t6);
  const {
    isLoadingHistory,
    contentTabs: openTabs,
    addOrUpdateContentTab
  } = contentTabs;
  let t7;
  let t8;
  if ($[23] !== addOrUpdateContentTab || $[24] !== authReady || $[25] !== fileVersions || $[26] !== isLoadingFiles || $[27] !== isLoadingHistory || $[28] !== openTabs || $[29] !== sessionId) {
    t7 = () => {
      if (!authReady || !sessionId) {
        return;
      }
      if (isLoadingHistory || isLoadingFiles) {
        return;
      }
      if (xmlRestoreAttemptedRef.current.has(sessionId)) {
        return;
      }
      const hasXmlTab = openTabs.some(_temp);
      if (hasXmlTab) {
        xmlRestoreAttemptedRef.current.add(sessionId);
        return;
      }
      const latestXml = latestXmlVersion(fileVersions);
      if (!latestXml?.blob_path) {
        xmlRestoreAttemptedRef.current.add(sessionId);
        return;
      }
      xmlRestoreAttemptedRef.current.add(sessionId);
      apiClient.getFileContent(latestXml.blob_path).then(fileData => {
        if (fileData?.content) {
          console.log(`[XML fallback] Restored XML from versions-index: ${latestXml.filename} v${latestXml.version}`);
          addOrUpdateContentTab("xml", fileData.content, latestXml.filename || "ODM XML");
        }
      }).catch(_temp2);
    };
    t8 = [authReady, sessionId, isLoadingFiles, isLoadingHistory, openTabs, addOrUpdateContentTab, fileVersions];
    $[23] = addOrUpdateContentTab;
    $[24] = authReady;
    $[25] = fileVersions;
    $[26] = isLoadingFiles;
    $[27] = isLoadingHistory;
    $[28] = openTabs;
    $[29] = sessionId;
    $[30] = t7;
    $[31] = t8;
  } else {
    t7 = $[30];
    t8 = $[31];
  }
  useEffect(t7, t8);
  let t9;
  if ($[32] !== allFileVersions || $[33] !== assessmentContext || $[34] !== chat || $[35] !== chatMode || $[36] !== contentTabs || $[37] !== conversationHistory || $[38] !== fileVersions || $[39] !== fileVersionsLoadFailed || $[40] !== isConnected || $[41] !== isLoading || $[42] !== isLoadingFiles || $[43] !== refreshFileVersions || $[44] !== resetSession || $[45] !== services || $[46] !== sessionId || $[47] !== setSessionId || $[48] !== setUploadedFiles || $[49] !== streamingSessionId || $[50] !== switchSession || $[51] !== uploadedFiles) {
    t9 = {
      chatMode,
      sessionId,
      setSessionId,
      resetSession,
      switchSession,
      isConnected,
      isLoading,
      services,
      contentTabs,
      chat,
      conversationHistory,
      fileVersions,
      allFileVersions,
      isLoadingFiles,
      fileVersionsLoadFailed,
      refreshFileVersions,
      uploadedFiles,
      setUploadedFiles,
      assessmentContext,
      setAssessmentContext,
      streamingSessionId
    };
    $[32] = allFileVersions;
    $[33] = assessmentContext;
    $[34] = chat;
    $[35] = chatMode;
    $[36] = contentTabs;
    $[37] = conversationHistory;
    $[38] = fileVersions;
    $[39] = fileVersionsLoadFailed;
    $[40] = isConnected;
    $[41] = isLoading;
    $[42] = isLoadingFiles;
    $[43] = refreshFileVersions;
    $[44] = resetSession;
    $[45] = services;
    $[46] = sessionId;
    $[47] = setSessionId;
    $[48] = setUploadedFiles;
    $[49] = streamingSessionId;
    $[50] = switchSession;
    $[51] = uploadedFiles;
    $[52] = t9;
  } else {
    t9 = $[52];
  }
  const value = t9;
  let t10;
  if ($[53] !== children || $[54] !== value) {
    t10 = <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
    $[53] = children;
    $[54] = value;
    $[55] = t10;
  } else {
    t10 = $[55];
  }
  return t10;
}
function _temp2(e) {
  console.log("[XML fallback] versions-index fetch failed:", e);
}
function _temp(t) {
  return t.type === "xml" && t.content?.trim();
}